/**
 * @file contexts/AuthContext.js
 * @description Auth context for React — cookie-based authentication.
 * Access tokens are in HttpOnly cookies (invisible to JS).
 * On 401 TOKEN_EXPIRED, silently calls /refresh to get a new access token.
 */
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
// Wave 2 §2.9: the offline queue follows the session (deadline banner, resume a held flush, sign-out).
import { notifySignedIn, notifySignedOut } from '../offline/syncEngine';

const AuthContext = createContext();

const API = process.env.REACT_APP_API_URL || '/api';

/**
 * authFetch — wrapper around fetch that:
 *   1. Sends cookies with every request (credentials: 'include')
 *   2. On 401 TOKEN_EXPIRED, calls /refresh and retries once
 */
async function authFetch(url, options = {}) {
  const opts = {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {})
    }
  };

  let response = await fetch(url, opts);

  // If access token expired, try refreshing
  if (response.status === 401) {
    const body = await response.json().catch(() => ({}));
    // Refresh on expired token OR missing token (browser deletes cookie after 15min maxAge)
    const shouldTryRefresh = !body.code || body.code === 'TOKEN_EXPIRED';
    if (shouldTryRefresh) {
      const refreshRes = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        credentials: 'include'
      });

      if (refreshRes.ok) {
        // Retry the original request with new cookie
        response = await fetch(url, opts);
      }
    }
  }

  return response;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  /** Check if we have a valid session on page load */
  const checkAuth = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/auth/me`);
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        notifySignedIn(data.session || null);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  /** Login — POST credentials, server sets cookies */
  const login = async (email, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    const data = await res.json();
    if (!res.ok) {
      // Attach the server's machine code + email so callers can branch
      // (e.g. EMAIL_NOT_VERIFIED → offer to resend the verification link).
      const err = new Error(data.error || 'Login failed');
      err.code = data.code;
      err.email = data.email;
      throw err;
    }
    setUser(data.user);
    notifySignedIn(data.session || null);
    return data;
  };

  /**
   * Register — POST details. When email verification is enforced, the server
   * responds with { emailVerificationRequired: true } and does NOT log the user
   * in; the caller shows a "check your email" screen. Otherwise the server sets
   * cookies and we set the user immediately (today's behavior).
   */
  const register = async (details) => {
    const res = await fetch(`${API}/auth/register`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(details)
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Registration failed');
    if (!data.emailVerificationRequired) {
      setUser(data.user);
    }
    return data;
  };

  /**
   * Verify an email via the token from the emailed link. On success the server
   * logs the user in (sets cookies), so we set the user here too.
   */
  const verifyEmail = async (token) => {
    const res = await fetch(`${API}/auth/verify-email`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Verification failed');
    if (data.user) setUser(data.user);
    return data;
  };

  /** Resend the verification link. Server always returns a generic success. */
  const resendVerification = async (email) => {
    const res = await fetch(`${API}/auth/resend-verification`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Could not resend verification');
    return data;
  };

  // =========================================================================
  // Passkey / Face ID (WebAuthn) — ADDITIVE. These sit alongside password
  // login; they never replace it. On a successful passkey sign-in the SERVER
  // sets the exact same session cookies password login sets, and we mirror the
  // password path here by calling setUser(data.user).
  // =========================================================================

  /** True only when the browser can actually do WebAuthn. */
  const passkeySupported = () =>
    typeof window !== 'undefined' &&
    !!window.PublicKeyCredential &&
    browserSupportsWebAuthn();

  /**
   * Sign in with Face ID / Touch ID / Windows Hello (usernameless).
   * Throws on failure so the caller can fall back to the password form.
   */
  const loginWithPasskey = async () => {
    const optRes = await fetch(`${API}/auth/passkey/auth/options`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    if (!optRes.ok) throw new Error('Could not start Face ID sign-in');
    const options = await optRes.json();

    // Opens the OS Face ID / passkey prompt. Throws NotAllowedError if the user
    // dismisses it — the caller treats that as a quiet cancel.
    const assertion = await startAuthentication({ optionsJSON: options });

    const verRes = await fetch(`${API}/auth/passkey/auth/verify`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(assertion),
    });
    const data = await verRes.json().catch(() => ({}));
    if (!verRes.ok) {
      const err = new Error(data.error || 'Face ID sign-in failed');
      err.code = data.code;
      throw err;
    }
    setUser(data.user);
    return data;
  };

  /**
   * Set up a passkey on THIS device for the signed-in user.
   * @param {string} [deviceName] optional friendly label
   */
  const registerPasskey = async (deviceName) => {
    const optRes = await fetch(`${API}/auth/passkey/register/options`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    });
    if (!optRes.ok) throw new Error('Could not start Face ID setup');
    const options = await optRes.json();

    const attestation = await startRegistration({ optionsJSON: options });

    const verRes = await fetch(`${API}/auth/passkey/register/verify`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ credential: attestation, deviceName }),
    });
    const data = await verRes.json().catch(() => ({}));
    if (!verRes.ok) throw new Error(data.error || 'Face ID setup failed');
    return data;
  };

  /** List this account's registered Face ID / passkey devices (fresh from server) */
  const listPasskeys = async () => {
    const res = await fetch(`${API}/auth/passkey/credentials`, {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Could not load your Face ID devices');
    const data = await res.json().catch(() => ({}));
    return (data && data.credentials) || [];
  };

  /** Remove one registered Face ID / passkey device by id */
  const removePasskey = async (id) => {
    const res = await fetch(`${API}/auth/passkey/credentials/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not remove that device');
    return data;
  };

  /** Logout — POST to server, server clears cookies */
  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch {
      // Clear locally even if server call fails
    }
    setUser(null);
    notifySignedOut();
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    verifyEmail,
    resendVerification,
    logout,
    authFetch,
    // Passkey / Face ID (additive)
    passkeySupported,
    loginWithPasskey,
    registerPasskey,
    listPasskeys,
    removePasskey,
    // Google Sign-In (LINK ONLY — a plain browser redirect, not a fetch call;
    // the backend handles the whole OAuth round trip and redirects back here)
    getGoogleLoginUrl: () => `${API}/auth/google`,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

/** useAuth hook — shortcut to access auth context */
export function useAuth() {
  return useContext(AuthContext);
}

export default AuthContext;